import {
  DialogLayout,
  ITableColumn,
  PageTitle,
  PaginatedTable,
} from '../../../presentation/components';
import {
  domainChildColumns,
  domainColumns,
  ipColumns,
  openPortChildColumns,
  openPortsColumns,
  vulnerabilitiesChildColumns,
  vulnerabilitiesColumns,
} from './risk-profile-grid-columns';
import {
  getPaginatedNodes,
  useGetEntitiesDomainsQuery,
  useIpAddressesAttackSurfaceQuery,
  useIpPortAttackSurfaceQuery,
  useVulnerabilitiesAttackSurfaceQuery,
} from '@converge/graphql';

import { CustomPaletteColor } from '../../../theme';
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface IEntitiesModalProps {
  submissionUuid: string;
  onClose: () => void;
  totalRows?: number;
}

const usePagination = () => {
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  return {
    currentPage,
    setCurrentPage,
    rowsPerPage,
    setRowsPerPage,
  };
};

export const EntitiesDomainModal = ({
  submissionUuid,
  onClose,
}: IEntitiesModalProps) => {
  const { currentPage, setCurrentPage, rowsPerPage, setRowsPerPage } =
    usePagination();

  const { t } = useTranslation();

  const { totalDomains, domains, loading } = useGetEntitiesDomainsQuery({
    submissionUuid,
    pageNumber: currentPage,
    pageSize: rowsPerPage,
  });

  return (
    <EntitiesCommonModal
      onClose={onClose}
      columns={domainColumns}
      childColumns={domainChildColumns}
      title={t('underwriting.entities.title', 'Domains')}
      subTitle={t('underwriting.entities.domain.title', '')}
      emptyRowMessage={t(
        'underwriting.entities.domain.emptyMessage',
        'No domains found'
      )}
      rows={domains}
      totalRows={totalDomains}
      getChildRows={(row) => getPaginatedNodes(row?.subDomains)}
      currentPage={currentPage}
      rowsPerPage={rowsPerPage}
      handleChangeRowsPerPage={(newRowsPerPage) => {
        setRowsPerPage(newRowsPerPage);
        setCurrentPage(0);
      }}
      handleChangePage={setCurrentPage}
      loading={loading && !domains.length}
    />
  );
};

export const EntitiesIpAddressesModal = ({
  submissionUuid,
  onClose,
  totalRows,
}: IEntitiesModalProps) => {
  const { currentPage, setCurrentPage, rowsPerPage, setRowsPerPage } =
    usePagination();

  const { t } = useTranslation();

  // const { totalIpAddresses, ipAddresses, loading } =
  //   useGetEntitiesIpAddressesQuery({
  //     submissionUuid,
  //     pageNumber: currentPage,
  //     pageSize: rowsPerPage,
  //   });

  const { loading: ipQueryLoading, data } = useIpAddressesAttackSurfaceQuery({
    entityUuid: submissionUuid,
    pageNumber: currentPage,
    pageSize: rowsPerPage,
  });

  return (
    <EntitiesCommonModal
      onClose={onClose}
      columns={ipColumns}
      title={t('IP Addresses')}
      subTitle={t('underwriting.entities.ip.title', '')}
      emptyRowMessage={t(
        'underwriting.entities.ip.emptyMessage',
        'No IP addresses found'
      )}
      rows={data?.ipAddressesAttackSurface.edges.map(
        (edge: { node: any }) => edge.node
      )}
      totalRows={totalRows}
      expandable={false}
      currentPage={currentPage}
      rowsPerPage={rowsPerPage}
      handleChangeRowsPerPage={(newRowsPerPage) => {
        setRowsPerPage(newRowsPerPage);
        setCurrentPage(0);
      }}
      handleChangePage={setCurrentPage}
      loading={ipQueryLoading}
    />
  );
};

export const EntitiesIpAddressOpenPortsModal = ({
  submissionUuid,
  onClose,
  totalRows,
}: IEntitiesModalProps) => {
  const { currentPage, setCurrentPage, rowsPerPage, setRowsPerPage } =
    usePagination();

  const { t } = useTranslation();
  const { loading: ipQueryLoading, data } = useIpPortAttackSurfaceQuery({
    entityUuid: submissionUuid,
    pageNumber: currentPage,
    pageSize: rowsPerPage,
  });

  return (
    <EntitiesCommonModal
      onClose={onClose}
      columns={openPortsColumns}
      childColumns={openPortChildColumns}
      title={t('underwriting.entities.title', 'Open ports')}
      subTitle={t('underwriting.entities.openPorts.title', '')}
      emptyRowMessage={t(
        'underwriting.entities.openPorts.emptyMessage',
        'No open ports found'
      )}
      rows={data?.portsAttackSurface.edges}
      totalRows={data?.portsAttackSurface?.pageInfo?.totalResults}
      getChildRows={(row) => row?.node?.ipAddresses ?? []}
      currentPage={currentPage}
      rowsPerPage={rowsPerPage}
      handleChangeRowsPerPage={(newRowsPerPage) => {
        setRowsPerPage(newRowsPerPage);
        setCurrentPage(0);
      }}
      handleChangePage={setCurrentPage}
      loading={ipQueryLoading}
    />
  );
};

export const EntitiesVulnerabilitiesModal = ({
  submissionUuid,
  onClose,
  totalRows,
}: IEntitiesModalProps) => {
  const { currentPage, setCurrentPage, rowsPerPage, setRowsPerPage } =
    usePagination();

  const { t } = useTranslation();

  const { loading, data } = useVulnerabilitiesAttackSurfaceQuery({
    entityUuid: submissionUuid,
    pageNumber: currentPage,
    pageSize: rowsPerPage,
  });

  return (
    <EntitiesCommonModal
      onClose={onClose}
      columns={vulnerabilitiesColumns}
      childColumns={vulnerabilitiesChildColumns}
      title={t('underwriting.entities.title', 'Vulnerabilities')}
      subTitle={t('underwriting.entities.vulnerabilities.title', '')}
      emptyRowMessage={t(
        'underwriting.entities.vulnerabilities.emptyMessage',
        'No vulnerabilities found'
      )}
      rows={data?.vulnerabilitiesAttackSurface.edges}
      totalRows={totalRows}
      getChildRows={(row) =>
        row?.node?.software?.edges?.map((edge: { node: any }) => edge.node) ??
        []
      }
      currentPage={currentPage}
      rowsPerPage={rowsPerPage}
      handleChangeRowsPerPage={(newRowsPerPage) => {
        setRowsPerPage(newRowsPerPage);
        setCurrentPage(0);
      }}
      handleChangePage={setCurrentPage}
      loading={loading}
    />
  );
};

interface IEntitiesCommonModalProps<R, CR> {
  onClose: () => void;
  columns: ITableColumn<R, void>[];
  expandable?: boolean;
  childColumns?: ITableColumn<CR, R>[];
  title: string;
  subTitle: string;
  emptyRowMessage: string;
  rows: R[] | undefined;
  totalRows: number | undefined;
  getChildRows?: (row: R) => CR[];
  currentPage: number;
  rowsPerPage: number;
  handleChangeRowsPerPage: (newRowsPerPage: number) => void;
  handleChangePage: (newPage: number) => void;
  loading?: boolean;
}

const EntitiesCommonModal = <R, CR>({
  onClose,
  columns,
  expandable = true,
  childColumns,
  title,
  subTitle,
  emptyRowMessage,
  rows = [],
  totalRows = 0,
  getChildRows,
  currentPage,
  rowsPerPage,
  handleChangeRowsPerPage,
  handleChangePage,
  loading = false,
}: IEntitiesCommonModalProps<R, CR>) => {
  return (
    <DialogLayout
      open
      onClose={onClose}
      showCloseButton={true}
      onCloseIconClick={onClose}
      header={
        <PageTitle color={CustomPaletteColor.Secondary}>{title}</PageTitle>
      }
      bodyText={subTitle}
      fullWidth
      maxWidth="lg"
      body={
        <PaginatedTable
          columns={columns}
          rows={rows}
          emptyRowMessage={loading ? 'Loading data...' : emptyRowMessage}
          expandable={expandable}
          getChildRows={getChildRows}
          childColumns={childColumns}
          enablePagination
          rowsPerPage={rowsPerPage}
          currentPage={currentPage}
          totalRows={totalRows}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
          handleChangePage={handleChangePage}
        />
      }
    />
  );
};
